import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";

const GOOGLE_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export const AuthModal = () => {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register, googleLogin, presetRole, apiError } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "buyer" });

  useEffect(() => {
    if (authOpen) setForm((f) => ({ ...f, role: presetRole || "buyer" }));
  }, [authOpen, presetRole]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGoogle = async (cred) => {
    try {
      await googleLogin(cred.credential);
      toast.success(t("login"));
      setAuthOpen(false);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === "login") {
        await login(form.email, form.password);
        toast.success(t("login"));
      } else {
        const u = await register(form);
        toast.success(t("register"));
        if (u?.role === "seller") navigate("/seller");
      }
      setAuthOpen(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { v: "buyer", l: t("role_buyer") },
    { v: "seller", l: t("role_seller") },
    { v: "delivery", l: t("role_delivery") },
  ];

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="auth-modal">
        <DialogTitle className="sr-only">{authMode === "login" ? t("login") : t("register")}</DialogTitle>
        <DialogDescription className="sr-only">AGOO MARKET authentication</DialogDescription>
        <div className="bg-emerald-800 px-8 py-6 text-white">
          <h2 className="font-display font-extrabold text-2xl">{authMode === "login" ? t("login") : t("register")}</h2>
          <p className="text-emerald-100 text-sm mt-1">AGOO MARKET — Lomé</p>
        </div>
        <form onSubmit={submit} className="px-8 py-6 space-y-4">
          {GOOGLE_ID ? (
            <div className="flex justify-center" data-testid="google-login-wrap">
              <GoogleLogin onSuccess={handleGoogle} onError={() => toast.error(t("google_failed"))} text="continue_with" locale={lang} width="336" />
            </div>
          ) : (
            <p className="text-xs text-center text-stone-400" data-testid="google-not-configured">{t("google_soon")}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-stone-400">
            <span className="flex-1 h-px bg-stone-200" />{t("or")}<span className="flex-1 h-px bg-stone-200" />
          </div>
          {authMode === "register" && (
            <>
              <div>
                <Label className="text-xs">{t("name")}</Label>
                <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="auth-name" />
              </div>
              <div>
                <Label className="text-xs">{t("phone")}</Label>
                <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+228 ..." data-testid="auth-phone" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("role")}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {roles.map((r) => (
                    <button key={r.v} type="button" onClick={() => upd("role", r.v)}
                      data-testid={`auth-role-${r.v}`}
                      className={`text-sm py-2 rounded-md border transition-colors ${form.role === r.v ? "border-emerald-800 bg-emerald-50 text-emerald-800 font-semibold" : "border-stone-300 text-stone-600"}`}>
                      {r.l}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">{t("email")}</Label>
            <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="auth-email" />
          </div>
          <div>
            <Label className="text-xs">{t("password")}</Label>
            <Input type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} required data-testid="auth-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-mango-600 hover:bg-mango-700 rounded-full" data-testid="auth-submit">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {authMode === "login" ? t("login") : t("register")}
          </Button>
          <p className="text-center text-sm text-stone-500">
            {authMode === "login" ? t("no_account") : t("have_account")}{" "}
            <button type="button" className="text-emerald-800 font-semibold" onClick={() => setAuthMode(authMode === "login" ? "register" : "login")} data-testid="auth-switch">
              {authMode === "login" ? t("register") : t("login")}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
